import { useEffect } from "react";

import { BigNumber } from "bignumber.js";

import { Card, Input, Notification, Select } from "@stellar/design-system";

import { AssetAmount } from "@/components/AssetAmount";
import { InfoTooltip } from "@/components/InfoTooltip";
import { Title } from "@/components/Title";

import { useAssetsByWallet } from "@/apiQueries/useAssetsByWallet";
import { useRegistrationContactTypes } from "@/apiQueries/useRegistrationContactTypes";
import { useVerificationTypes } from "@/apiQueries/useVerificationTypes";
import { useWallets } from "@/apiQueries/useWallets";

import { formatRegistrationContactType } from "@/helpers/formatRegistrationContactType";
import { formatUploadedFileDisplayName } from "@/helpers/formatUploadedFileDisplayName";

import { useAllBalances } from "@/hooks/useAllBalances";
import { useRedux } from "@/hooks/useRedux";

import {
  AccountBalanceItem,
  ApiAsset,
  ApiWallet,
  Disbursement,
  DisbursementStep,
  hasWallet,
  isUserManagedWalletEnabled,
  NONE_VERIFICATION_VALUE,
  RegistrationContactType,
  VerificationFieldMap,
} from "@/types";

import "./styles.scss";

const SDP_EMBEDDED_WALLET_NAME = "embedded wallet";

const isSdpEmbeddedWallet = (walletName: string): boolean =>
  walletName.trim().toLowerCase() === SDP_EMBEDDED_WALLET_NAME;

interface DisbursementDetailsProps {
  variant: DisbursementStep;
  details?: Disbursement;
  futureBalance?: number;
  csvFile?: File;
  /**
   * Turns the editable fields of the summary (name, language) into inputs, so a
   * reviewer can correct a draft in place. Ignored by the "edit" variant, which is
   * already a form.
   */
  isEditing?: boolean;
  onChange?: (state: Disbursement) => void;
  onValidate?: (isValid: boolean) => void;
}

const initDetails: Disbursement = {
  id: "",
  name: "",
  registrationContactType: undefined,
  asset: {
    id: "",
    code: "",
  },
  wallet: {
    id: "",
    name: "",
  },
  verificationField: "",
  createdAt: "",
  status: "DRAFT",
  statusHistory: [],
  receiverRegistrationMessageTemplate: "",
  stats: undefined,
};

enum FieldId {
  NAME = "name",
  REGISTRATION_CONTACT_TYPE = "registration_contact_type",
  LANGUAGE = "language",
}

const isDisbursementValid = (inputs: Disbursement): boolean => {
  if (!inputs.name) {
    return false;
  }

  if (!inputs.registrationContactType) {
    return false;
  }

  if (!inputs.asset.code) {
    return false;
  }

  if (!hasWallet(inputs.registrationContactType)) {
    if (!inputs.wallet.id) {
      return false;
    }
    // verificationField is validated in Step 3 (DisbursementVerification)
  }

  return true;
};

interface DerivedFormState {
  isWalletAddressProvided: boolean;
  isWalletRegistrationEnabled: boolean;
  registrationOptions: RegistrationContactType[];
  walletOptions: ApiWallet[];
  assetOptions: ApiAsset[];
  verificationOptions: string[];
  selectValues: {
    registrationContactType: string;
    walletId: string;
    assetId: string;
    verificationField: string;
  };
  labels: {
    walletProvider: string;
    verification: string;
  };
  disabled: {
    registrationContactType: boolean;
    wallet: boolean;
    asset: boolean;
    verification: boolean;
  };
}

interface DeriveFormStateArgs {
  details: Disbursement;
  wallets?: ApiWallet[];
  walletAssets?: ApiAsset[];
  registrationContactTypes?: RegistrationContactType[];
  verificationTypes?: string[];
  allBalances?: AccountBalanceItem[];
  loading: {
    registrationContactTypes: boolean;
    wallets: boolean;
    walletAssets: boolean;
    verificationTypes: boolean;
  };
}

const deriveFormState = ({
  details,
  wallets,
  walletAssets,
  registrationContactTypes,
  verificationTypes,
  allBalances,
  loading,
}: DeriveFormStateArgs): DerivedFormState => {
  const isWalletAddressProvided = hasWallet(details.registrationContactType);
  const isWalletRegistrationEnabled = isUserManagedWalletEnabled(wallets);
  const isEmbeddedWallet = isSdpEmbeddedWallet(details.wallet.name);

  const enabledWallets = (wallets ?? []).filter((wallet) => wallet.enabled);
  const enabledUserManagedWallets = enabledWallets.find((wallet) => wallet.user_managed);

  const registrationOptions =
    registrationContactTypes?.filter((type) => !hasWallet(type) || isWalletRegistrationEnabled) ??
    [];

  const walletOptions = enabledWallets.filter((wallet) =>
    isWalletAddressProvided ? true : !wallet.user_managed,
  );

  const allVerificationTypes = verificationTypes ?? [];
  const verificationOptions = (() => {
    if (isEmbeddedWallet) {
      return [NONE_VERIFICATION_VALUE, ...allVerificationTypes];
    }

    if (isWalletAddressProvided) {
      return [NONE_VERIFICATION_VALUE];
    }

    return allVerificationTypes;
  })();

  const assetOptions = (walletAssets ?? []).filter((asset) => {
    if (asset.code === "XLM" && asset.issuer === "") {
      return true;
    }

    return !!allBalances?.find(
      (balance) => balance.assetCode === asset.code && balance.assetIssuer === asset.issuer,
    );
  });

  const selectValues = {
    registrationContactType: details.registrationContactType ?? "",
    walletId: isWalletAddressProvided ? (enabledUserManagedWallets?.id ?? "") : details.wallet.id,
    assetId: details.asset.id,
    verificationField: isWalletAddressProvided
      ? NONE_VERIFICATION_VALUE
      : (details.verificationField ?? ""),
  };

  const labels = {
    walletProvider: details.wallet.name
      ? details.wallet.name
      : isWalletAddressProvided
        ? (enabledUserManagedWallets?.name ?? "-")
        : "-",
    verification: details.verificationField
      ? VerificationFieldMap[details.verificationField] || details.verificationField
      : isWalletAddressProvided || isEmbeddedWallet
        ? NONE_VERIFICATION_VALUE
        : "-",
  };

  const disabled = {
    registrationContactType: loading.registrationContactTypes,
    wallet:
      loading.wallets ||
      !registrationContactTypes ||
      !details.registrationContactType ||
      isWalletAddressProvided,
    asset:
      loading.walletAssets ||
      (!details.wallet.id && (!isWalletAddressProvided || !details.registrationContactType)),
    verification:
      loading.verificationTypes ||
      !registrationContactTypes ||
      !details.registrationContactType ||
      isWalletAddressProvided,
  };

  return {
    isWalletAddressProvided,
    isWalletRegistrationEnabled,
    registrationOptions,
    walletOptions,
    assetOptions,
    verificationOptions,
    selectValues,
    labels,
    disabled,
  };
};

export const DisbursementDetails: React.FC<DisbursementDetailsProps> = ({
  variant,
  details: rawDetails = initDetails,
  futureBalance = 0,
  csvFile,
  isEditing = false,
  onChange,
  onValidate,
}: DisbursementDetailsProps) => {
  const details: Disbursement = {
    ...initDetails,
    ...rawDetails,
    wallet: rawDetails.wallet ?? initDetails.wallet,
    asset: rawDetails.asset ?? initDetails.asset,
  };
  const { organization } = useRedux("organization");
  const supportedLanguages = organization.data.supportedLanguages;
  const languageLabels: Record<string, string> = Object.fromEntries(
    supportedLanguages.map(({ code, label }) => [code, label]),
  );

  const { data: wallets, error: walletsError, isLoading: isWalletsLoading } = useWallets({});
  const {
    data: registrationContactTypes,
    error: registrationContactTypesError,
    isLoading: areRegistrationContactTypesLoading,
  } = useRegistrationContactTypes();

  const {
    data: walletAssets,
    error: walletError,
    isFetching: isWalletAssetsFetching,
  } = useAssetsByWallet({
    walletId: details.wallet.id,
    registrationContactType: details.registrationContactType,
  });

  const {
    data: verificationTypes,
    error: verificationTypesError,
    isFetching: isVerificationTypesFetching,
  } = useVerificationTypes();

  const { allBalances } = useAllBalances();

  const derived = deriveFormState({
    details,
    wallets,
    walletAssets,
    registrationContactTypes,
    verificationTypes,
    allBalances,
    loading: {
      registrationContactTypes: areRegistrationContactTypesLoading,
      wallets: isWalletsLoading,
      walletAssets: isWalletAssetsFetching,
      verificationTypes: isVerificationTypesFetching,
    },
  });

  const apiErrors = [
    registrationContactTypesError?.message,
    walletsError?.message,
    walletError?.message,
    verificationTypesError?.message,
  ]
    .filter(Boolean)
    .map(String);

  const updateDetails = (next: Partial<Disbursement>) => {
    const updatedDetails = {
      ...details,
      ...next,
    };

    onChange?.(updatedDetails);
    onValidate?.(isDisbursementValid(updatedDetails));
  };

  const handleRegistrationContactTypeChange = (value: string) => {
    const registrationContactType = registrationContactTypes?.find((type) => type === value);
    const nextHasWallet = hasWallet(registrationContactType);
    const currentHasWallet = hasWallet(details.registrationContactType);

    const updates: Partial<Disbursement> = {
      registrationContactType,
    };

    if (!registrationContactType || nextHasWallet) {
      updates.wallet = { id: "", name: "" };
      updates.verificationField = "";
    }

    if (!registrationContactType || nextHasWallet !== currentHasWallet) {
      updates.asset = { id: "", code: "" };
    }

    updateDetails(updates);
  };

  // Auto-select VIA Wallet when no wallet is chosen yet. Only while creating a
  // disbursement — an existing draft already has its wallet, asset and contact type set,
  // and must not have them silently rewritten while it is being reviewed.
  useEffect(() => {
    if (
      variant === "edit" &&
      !details.wallet.id &&
      !hasWallet(details.registrationContactType) &&
      wallets?.length
    ) {
      const viaWallet = wallets
        .filter((w) => w.enabled && !w.user_managed)
        .find((w) => w.name === "VIA Wallet");
      if (viaWallet) {
        updateDetails({ wallet: { id: viaWallet.id, name: viaWallet.name } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets, details.wallet.id, details.registrationContactType]);

  // Auto-select the first available asset when options load and none is selected yet.
  // We gate on `allBalances !== undefined` to avoid a race condition where walletAssets
  // loads before allBalances: without the gate, only XLM (which bypasses the balance
  // filter) would appear in assetOptions and get selected, even when USDC is available.
  useEffect(() => {
    if (
      variant === "edit" &&
      !details.asset.id &&
      allBalances !== undefined &&
      derived.assetOptions.length > 0
    ) {
      const firstAsset = derived.assetOptions[0];
      updateDetails({ asset: { id: firstAsset.id, code: firstAsset.code } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAssets, allBalances, details.wallet.id, details.asset.id]);

  // Auto-select the first registration contact type when options load and none is selected yet
  useEffect(() => {
    if (
      variant === "edit" &&
      !details.registrationContactType &&
      derived.registrationOptions.length > 0
    ) {
      handleRegistrationContactTypeChange(derived.registrationOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationContactTypes, details.registrationContactType]);

  const handleNameChange = (value: string) => {
    updateDetails({ name: value });
  };

  const handleLanguageChange = (value: string) => {
    updateDetails({ language: value });
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { id, value } = event.target;

    switch (id) {
      case FieldId.REGISTRATION_CONTACT_TYPE:
        handleRegistrationContactTypeChange(value);
        break;
      case FieldId.NAME:
        handleNameChange(value);
        break;
      case FieldId.LANGUAGE:
        handleLanguageChange(value);
        break;
      default:
        break;
    }
  };

  // Total the batch will pay out. Comes from the disbursement statistics once a CSV has
  // been uploaded; before that there is nothing to total up.
  const totalAmount = details.stats?.totalAmount ?? "0";

  const renderSummaryFields = () => (
    <>
      <div>
        <label className="Label Label--sm">Registration Contact Type</label>
        <div className="DisbursementDetailsFields__value">
          {formatRegistrationContactType(details.registrationContactType)}
        </div>
      </div>

      <div>
        <label className="Label Label--sm">Wallet provider</label>
        <div className="DisbursementDetailsFields__value">{derived.labels.walletProvider}</div>
      </div>

      <div>
        <label className="Label Label--sm">Asset</label>
        <div className="DisbursementDetailsFields__value">{details.asset.code}</div>
      </div>

      <div>
        <label className="Label Label--sm">Verification Type</label>
        <div className="DisbursementDetailsFields__value">{derived.labels.verification}</div>
      </div>

      {isEditing ? (
        <Input
          id={FieldId.NAME}
          label="Disbursement name"
          fieldSize="sm"
          onChange={handleFieldChange}
          value={details.name}
        />
      ) : (
        <div>
          <label className="Label Label--sm">Disbursement name</label>
          <div className="DisbursementDetailsFields__value">{details.name}</div>
        </div>
      )}

      {isEditing ? (
        <Select
          id={FieldId.LANGUAGE}
          label={
            <InfoTooltip infoText="Sets the language for receiver invitation messages and the verification flow. Receivers can switch to their preferred language later in their wallet.">
              Language
            </InfoTooltip>
          }
          fieldSize="sm"
          onChange={handleFieldChange}
          value={details.language ?? ""}
        >
          {supportedLanguages.map(({ code, label }) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </Select>
      ) : (
        <div>
          <label className="Label Label--sm">Language</label>
          <div className="DisbursementDetailsFields__value">
            {details.language ? (languageLabels[details.language] ?? details.language) : "-"}
          </div>
        </div>
      )}

      <div>
        <label className="Label Label--sm">Total disbursement amount</label>
        <div className="DisbursementDetailsFields__value">
          <AssetAmount amount={totalAmount} assetCode={details.asset.code} />
        </div>
      </div>

      <div>
        <label className="Label Label--sm">Future balance</label>
        <div
          className={`DisbursementDetailsFields__value ${
            BigNumber(futureBalance).gte(0) ? "" : "DisbursementDetailsFields__negative"
          }`}
        >
          <AssetAmount amount={futureBalance.toString()} assetCode={details.asset.code} />
        </div>
      </div>

      {variant === "confirmation" ? (
        <div>
          <label className="Label Label--sm">CSV</label>
          <div className="DisbursementDetailsFields__value">
            {csvFile ? formatUploadedFileDisplayName(csvFile) : ""}
          </div>
        </div>
      ) : null}
    </>
  );

  const renderFormFields = () => (
    <>
      <Select
        id={FieldId.REGISTRATION_CONTACT_TYPE}
        label="Registration contact type"
        fieldSize="sm"
        onChange={handleFieldChange}
        value={derived.selectValues.registrationContactType}
        disabled={derived.disabled.registrationContactType}
      >
        <option value="" disabled>
          Select...
        </option>
        {derived.registrationOptions.map((type) => (
          <option key={type} value={type}>
            {formatRegistrationContactType(type)}
          </option>
        ))}
      </Select>

      <Input
        id={FieldId.NAME}
        label="Disbursement name"
        fieldSize="sm"
        onChange={handleFieldChange}
        value={details.name}
      />

      <Select
        id={FieldId.LANGUAGE}
        label={
          <InfoTooltip infoText="Sets the language for receiver invitation messages and the verification flow. Receivers can switch to their preferred language later in their wallet.">
            Language
          </InfoTooltip>
        }
        fieldSize="sm"
        onChange={handleFieldChange}
        value={details.language ?? ""}
      >
        {supportedLanguages.map(({ code, label }) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </Select>
    </>
  );

  const renderContent = () => {
    if (variant === "preview" || variant === "confirmation") {
      return renderSummaryFields();
    }

    return renderFormFields();
  };

  return (
    <>
      {apiErrors.length ? (
        <Notification variant="error" title="Error" isFilled={true}>
          {apiErrors.map((errorMessage) => (
            <div key={`error-${errorMessage}`}>{errorMessage}</div>
          ))}
        </Notification>
      ) : null}

      <Card>
        <InfoTooltip infoText="Select each field and give your disbursement a unique name">
          <Title size="md">Disbursement details</Title>
        </InfoTooltip>

        <div className="DisbursementDetailsFields__inputs">{renderContent()}</div>
      </Card>
    </>
  );
};
